// Website leads: every form submitted on alignovaaligners.com (smile-check assessment, contact,
// partner registration), newest first. Rows expand to show every field the form sent.
import { Fragment, useEffect, useState } from 'react';
import { formatDateTime } from '@alignova/shared';
import { api } from '../../lib/api.ts';
import { t } from '../../i18n/index.ts';
import { Card, PageHeader } from '../../ui/primitives.tsx';

interface Lead {
  id: string;
  form: string | null;
  fields: Record<string, string>;
  createdAt: string;
}

// The assessment quiz sends its answers as q1..q5; label them with the site's questions.
const QUESTIONS: Record<string, string> = {
  q1: 'Who is the treatment for?',
  q2: 'What would you like to improve?',
  q3: 'Where are you in your decision?',
  q4: 'Biggest question',
  q5: 'Wants a free assessment from a doctor?',
};

export function LeadsPage() {
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<Lead[]>('/leads')
      .then(setLeads)
      .catch(() => setError('Failed to load website leads'));
  }, []);

  if (error) return <p className="text-status-urgent-fg">{error}</p>;

  return (
    <div>
      <PageHeader title={t('nav.leads')} subtitle="Assessment and enquiry forms submitted on alignovaaligners.com" />
      <Card className="overflow-x-auto p-0">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="px-4 py-3 font-medium">When</th>
              <th className="px-4 py-3 font-medium">Form</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">City</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {leads?.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-muted">
                  No submissions yet.
                </td>
              </tr>
            )}
            {leads?.map((l) => (
              <Fragment key={l.id}>
                <tr className="border-b border-line hover:bg-surface-2">
                  <td className="px-4 py-2 font-mono text-xs">{formatDateTime(l.createdAt)}</td>
                  <td className="px-4 py-2 capitalize">{l.form ?? '-'}</td>
                  <td className="px-4 py-2">{l.fields.name ?? '-'}</td>
                  <td className="px-4 py-2">{l.fields.email ? <a className="text-accent hover:underline" href={`mailto:${l.fields.email}`}>{l.fields.email}</a> : '-'}</td>
                  <td className="px-4 py-2">{l.fields.whatsapp ?? l.fields.phone ?? '-'}</td>
                  <td className="px-4 py-2">{l.fields.city ?? '-'}</td>
                  <td className="px-4 py-2 text-right">
                    <button className="text-xs text-accent hover:underline" onClick={() => setOpen(open === l.id ? null : l.id)}>
                      {open === l.id ? 'hide' : 'details'}
                    </button>
                  </td>
                </tr>
                {open === l.id && (
                  <tr className="bg-surface-2">
                    <td colSpan={7} className="px-4 py-3">
                      <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-[max-content_1fr]">
                        {Object.entries(l.fields).map(([k, v]) => (
                          <Fragment key={k}>
                            <dt className="text-muted">{QUESTIONS[k] ?? k}</dt>
                            <dd className="whitespace-pre-wrap">{v}</dd>
                          </Fragment>
                        ))}
                      </dl>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
